import { ClientSocket } from '../ClientSocket/index.js'
import okeResponse from 'okeoke.standardresponse'
import { OrderEngineCatalog } from '../Catalog/index.js'
import { Locations } from '../Locations/index.js'
import { Contents } from '../Contents/index.js'
import { User } from '../User/index.js'
import { Orders } from '../Orders/index'
import { localStorage } from 'okeoke.localstorage'
import { ExternalPayments } from '../ExternalPayments/index.js'
import { Shifts } from '../Trx/Shifts/index.js'
import { Transactions } from '../Trx/Transactions/index.js'
import { Documents } from '../Trx/Documents/index.js'
import { Items } from '../Trx/Items/index.js'
import { DocumentData } from '../Trx/DocumentData/index.js'
import cloneDeep from 'lodash.clonedeep'

var OrderEngineClient = function (endpoint, app, token, frameInfo = null, deviceParams = null, clientUID = null, sendMessageToWebView = null) {
  this.offlineEnabled = (['pos', 'kiosk', 'ecrPos'].includes(app))
  this.token = token
  this.tokenData = null
  this.userData = null
  this.localUser = null
  this.deviceParams = deviceParams
  this.sendMessageToWebView = sendMessageToWebView

  this.callbacks = {
    //onOrderListUpdated: {},
    onConnectionChange: {},
    onCatalogStateChange: {},
    onDictionaryLoaded: {},
    onLocationsLoaded: {},
    onOrdersInited: {},
    onBasketListUpdated: {},
    onProfileRefreshed: {},
    onDeviceStateUpdated: {}
  }

  this.gpsPosition = null
  this.catalogLoading = true
  this.catalogFirstLoad = true
  this.locationsModel = new Locations(endpoint, app)
  this.catalog = new OrderEngineCatalog(endpoint, this.locationsModel, app, deviceParams)
  this.clientSocket = new ClientSocket(endpoint, token, clientUID)
  this.orders = new Orders(this, this.clientSocket, this.catalog, app, this.offlineEnabled)
  this.externalPayments = new ExternalPayments(this.clientSocket, this, this.orders)
  this.user = new User(endpoint, token)
  this.contents = new Contents(endpoint)
  this.shifts = new Shifts(this.clientSocket, this)
  this.documents = new Documents(this.clientSocket, this)
  this.items = new Items(this.clientSocket, this)
  this.transactions = new Transactions(this.clientSocket, this)
  this.documentData = new DocumentData(this.clientSocket, this)

  this.setToken = (newToken) => {
    if (this.token !== newToken) {
      this.token = token
      this.user.setToken(newToken)
      this.clientSocket.setToken(newToken)
    }
  }

  this.checkForUpdateServiceWorker = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(function (registrations) {
        for (let registration of registrations) {
          registration.update()
        }
      })
    }
  }

  this.onConnectionChange = async (data) => {
    switch (data.state) {
      case 'Authorized':
        this.tokenData = data.data.data
        if (this.tokenData != null) {
          try {
            let existingDeviceData = JSON.parse(localStorage.getItem(`okeoke-${app || '0'}-device-data`))
            if (this.tokenData.deviceUUID !== existingDeviceData.deviceUUID) {
            }
          } catch (e) {
          }
          localStorage.setItem(`okeoke-${app || '0'}-device-data`, JSON.stringify(this.tokenData))
        }
        // Refresh locations on reconnect
        this.locationsModel.getLocations(true, this.token).then(() => {
          this.catalog.getCatalog()
        })
        if (this.tokenData.tokenType === 'user') {
          this.user.getProfile()
          this.user.getAddresses()
        } else {
          if (this.localUser != null) {
            //console.log('LOGIN USER:', this.localUser)
            this.setDeviceUser(this.localUser.userUUID || '', this.localUser.pinCode || '')
          }
        }
        this.checkForUpdateServiceWorker()       
        break
      case 'Disconnected':
        break
    }
    this.callCallbacks('onConnectionChange', data)
  }

  this.addCallback = (cbName, name, func) => {
    try {
      this.callbacks[cbName][name] = func
    } catch (e) {
      console.error('Invalid callback name in - add (Client)', cbName)
    }
  }

  this.removeCallback = (cbName, name) => {
    try {
      delete this.callbacks[cbName][name]
    } catch (e) {
      console.error('Invalid callback name - remove (Client)', cbName)
    }
  }

  this.callCallbacks = async (cbName, message) => {
    for (let callback of Object.values(this.callbacks[cbName] || {})) {
      if (typeof callback === 'function') {
        try {
          callback(message)
        } catch (e) {
          console.error(e)
        }
      }
    }
  }

  this.setLocationOpenState = (state) => {
    this.locationsModel.setLocationOpenState(state)
  }

  this.onLocationCloudDeviceUpdated = (data) => {
    this.locationsModel.setLocationCloudDevice(data)
  }

  this.setLocationCloudDevice = (data) => {
    this.locationsModel.setLocationCloudDevice(data)
  }

  this.setLocationExtendedPrepTime = (data) => {
    this.locationsModel.setLocationExtendedPrepTime(data)
  }

  this.onUpdatedBaskets = (data) => {
    if (data.success) {
      this.callCallbacks('onBasketListUpdated', { baskets: data.baskets })
    }
  }

  this.getUserOrders = (fromDate, toDate) => {
    return new Promise((resolve, reject) => {
      if (!this.clientSocket.authorized) {
        resolve(okeResponse(false, 503, 'Not connected', {}))
      } else {
        setTimeout(() => {
          resolve({ success: false, message: 'Timeout!', code: 408, data: { errMsgCode: 'request.error.timeout' } })
        }, 5000)
        this.clientSocket.ioclient.emit('getUserOrders', {
          filter: {
            fromDate,
            toDate
          }
        }, (data) => {
          resolve(data)
        })
      }
    })
  }

  this.getUserOrder = (orderUUID) => {
    return new Promise((resolve, reject) => {
      if (!this.clientSocket.authorized) {
        resolve(okeResponse(false, 503, 'Not connected', {}))
      } else {
        setTimeout(() => {
          resolve({ success: false, message: 'Timeout!', code: 408, data: { errMsgCode: 'request.error.timeout' } })
        }, 5000)
        this.clientSocket.ioclient.emit('getUserOrder', {
          orderUUID
        }, (data) => {
          resolve(data)
        })
      }
    })
  }

  this.onCatalogStateChange = (data) => {
    this.catalogState = data.state
    this.callCallbacks('onCatalogStateChange', data)
    if (data.state === 'Loaded' && !this.orders.inited) {
      this.orders.init(this.tokenData)
    }
  }

  this.onDictionaryLoaded = (data) => {
    this.callCallbacks('onDictionaryLoaded', data)
  }

  this.getLocalUsers = () => {
    return new Promise((resolve, reject) => {
      if (this.clientSocket.authorized) {
        setTimeout(() => {
          resolve({ success: false, message: 'Timeout!', code: 408, data: { errMsgCode: 'request.error.timeout' } })
        }, 5000)
        this.clientSocket.ioclient.emit('getLocalUsers', {}, (e) => {
          resolve(e)
        })
      } else {
        resolve({ success: false, message: 'No connection to server!' })
      }
    })
  }

  this.setDeviceUser = (userUUID, pinCode) => {
    //console.log({ userUUID, pinCode })
    this.localUser = { userUUID, pinCode }
    return new Promise((resolve, reject) => {
      if (this.clientSocket.authorized) {
        setTimeout(() => {
          resolve({ success: false, message: 'Timeout!', code: 408, data: { errMsgCode: 'request.error.timeout' } })
        }, 5000)
        this.clientSocket.ioclient.emit('setDeviceUser', { userUUID, pinCode }, (e) => {
          resolve(e)
        })
      } else {
        resolve({ success: false, message: 'No connection to server!' })
      }
    })
  }

  this.logDeviceError = (deviceUUID, severity, errorCode, message, info) => {
    return new Promise((resolve, reject) => {
      if (this.clientSocket.authorized) {
        setTimeout(() => {
          resolve({ success: false, message: 'Timeout!', code: 408, data: { errMsgCode: 'request.error.timeout' } })
        }, 5000)
        this.clientSocket.ioclient.emit('logDeviceError', {
          deviceUUID, severity, errorCode, message, info
        }, (e) => {
          resolve({ success: e.success, message: e.message })
        })
      } else {
        resolve({ success: false, message: 'No connection to server!' })
      }
    })
  }

  this.resolveDeviceError = (id) => {
    return new Promise((resolve, reject) => {
      if (this.clientSocket.authorized) {
        setTimeout(() => {
          resolve({ success: false, message: 'Timeout!', code: 408, data: { errMsgCode: 'request.error.timeout' } })
        }, 5000)
        this.clientSocket.ioclient.emit('resolveDeviceError', {
          id
        }, (e) => {
          resolve({ success: e.success, message: e.message })
        })
      } else {
        resolve({ success: false, message: 'No connection to server!' })
      }
    })
  }

  this.onDeviceStateUpdated = (data) => {
    this.callCallbacks('onDeviceStateUpdated', data)
  }

  this.setGpsPosition = (latitude, longitude) => {
    this.gpsPosition = {latitude, longitude}
    // this.refreshLocationDistances()
  }

  this.getTableByCode = (code) => {
    let table = this.catalog?.tables?.[code]
    if (table == null) {
      if (this.catalog?.tables != null) {
        table = Object.values(this.catalog.tables).find(t => t.shortID === code)
      }
    }
    if (table != null) return okeResponse(true, 200, 'Found', cloneDeep(table))
    else return okeResponse(false, 404, 'Not found', null)
  }

  this.onItemVisibilityChanged = (data) => {
    this.catalog.updateItemAvailability(data.data.uuid, data.data.locationUUID, data.data.visible, data.data.available)
  }

  this.onOrdersInited = (data) => {
    let activeBasket = this.orders.findBasketOrder()
    this.shifts.init()
    this.callCallbacks('onOrdersInited', { ...data, activeBasketUUID: activeBasket?.order?.uuid })
  }

  this.skipInited = (message = null, data = null) => {
    this.logDeviceError(this.tokenData.deviceUUID, 3, 'skipInited', message || 'Skip inited', data || {})
  }

  this.onShiftsInited = (data) => {
    this.items.init(data)
    this.transactions.init(data)
    this.documentData.init(data)
    this.documents.init(data)
  }

  this.onLocationsUpdated = (data) => {
    this.locationsModel.getLocations(true, this.token)
  }

  this.onLocationsLoaded = (data) => {
    this.callCallbacks('onLocationsLoaded', { locations: data.locations })
  }

  this.init = () => {
    let tokenData = localStorage.getItem(`okeoke-${app || '0'}-device-data`)
    if (tokenData != null && this.tokenData == null) {
      try {
        this.tokenData = JSON.parse(tokenData)
      } catch (e) {
        this.tokenData = null
      }
    }
    if(this.tokenData != null || !this.offlineEnabled) {
      this.locationsModel.getLocations(true, this.token).then(() => {
        this.catalog.getCatalog()
      })
    }
  }

  this.refreshCatalog = async () => {
    await this.catalog.getCatalog(true)
  }

  this.disable = () => {
    this.clientSocket.disable()
  }

  this.getOpenHoursDates = this.locationsModel.getOpenHoursDates
  this.getOpenHoursDefault = this.locationsModel.getOpenHoursDefault

  this.orders.addCallback('onOrdersInited', 'Client.self', this.onOrdersInited)

  this.locationsModel.addCallback('onLocationsLoaded', 'Client.self', this.onLocationsLoaded)

  this.catalog.addCallback('onCatalogStateChange', 'Client.self', this.onCatalogStateChange)
  this.catalog.addCallback('onDictionaryLoaded', 'Client.self', this.onDictionaryLoaded)
  this.catalog.getDictionary()

  this.clientSocket.addCallback('onConnectionChange', 'Client.self', this.onConnectionChange)

  this.clientSocket.addCallback('onLocationOpenStateUpdated', 'Client.self', this.setLocationOpenState)
  this.clientSocket.addCallback('onLocationCloudDeviceUpdated', 'Client.self', this.onLocationCloudDeviceUpdated)
  this.clientSocket.addCallback('onLocationExtendedPrepTimeUpdated', 'Client.self', this.setLocationExtendedPrepTime)

  this.clientSocket.addCallback('onItemVisibilityChanged', 'Client.self', this.onItemVisibilityChanged)
  this.clientSocket.addCallback('onDeviceStateUpdated', 'Client.self', this.onDeviceStateUpdated)
  this.clientSocket.addCallback('onLocationsUpdated', 'Client.self', this.onLocationsUpdated)

  this.shifts.addCallback('onShiftsInited', 'Client.self', this.onShiftsInited)

  //this.init()
  this.clientSocket.init(frameInfo)
}

export { OrderEngineClient }
